import { api } from './api'
import { MINUSFARBWAHL, t, VORZEICHENWAHL, ZAHLENFORMATWAHL, zahlenbeispiel } from './sprache'
import type { Minusfarbe, Sprachschluessel, Vorzeichenart, Zahlenformat } from './sprache'

/**
 * Was der Dienst über die eigene Zahlendarstellung weiß (7.2 Abschnitt 43,
 * R1315). `null` heißt: keine eigene Wahl, es gilt der Standard.
 *
 * **`gewaehlt: false` heißt: Es gibt noch gar keine** — dann übernimmt die
 * Anwendung einmal, was der Browser sich bisher gemerkt hat (R1316).
 */
export type Zahlendarstellungangabe = {
  gewaehlt: boolean
  vorzeichen: Vorzeichenart | null
  minusfarbe: Minusfarbe | null
  zahlenformat: Zahlenformat | null
}

/** Die Zahlendarstellung, wie sie gerade gilt. */
export type Zahlenstand = {
  vorzeichen: Vorzeichenart
  minusfarbe: Minusfarbe
  zahlenformat: Zahlenformat
}

/**
 * **Die eigene Zahlendarstellung beim Anmelden** (R1315, R1316).
 *
 * Hat der Anwender eine, gilt sie — eine Lücke darin ist der Standard. Hat er
 * noch keine, übernimmt die Anwendung einmal, was der Browser sich bisher
 * gemerkt hat: Wer Soll und Haben eingestellt hatte, findet nach dem Umzug ins
 * Profil nicht Plus und Minus vor.
 */
export async function eigeneZahlendarstellung(browser: Zahlenstand): Promise<Zahlenstand> {
  const angabe = await api.zahlendarstellung()

  if (!angabe.gewaehlt) {
    await api.zahlendarstellungSetzen(browser)
    return browser
  }

  return {
    vorzeichen: angabe.vorzeichen ?? 'plusminus',
    minusfarbe: angabe.minusfarbe ?? 'rot',
    zahlenformat: angabe.zahlenformat ?? 'sprache',
  }
}

export type Zahlenwahlangabe = {
  zahlenformat: Zahlenformat
  /** Ob Beträge mit Vorzeichen oder als Soll und Haben stehen. */
  vorzeichen: Vorzeichenart
  /** Ob ein negativer Betrag rot oder schwarz steht (R1015). */
  minusfarbe: Minusfarbe
  aufZahlenformat: (wahl: Zahlenformat) => void
  aufVorzeichen: (wahl: Vorzeichenart) => void
  aufMinusfarbe: (wahl: Minusfarbe) => void
}

/**
 * **Die Zahlendarstellung im Profil** (7.2 Abschnitt 43, R1314).
 *
 * Die drei Felder standen bis zum 2026-10-06 in der Fußzeile, unter jedem
 * Bildschirm. Der Auftraggeber: *„Die Fußzeile ist für viele Bereiche nicht
 * sinnvoll. Dort ist zwar die Sprache nützlich, aber die anderen Punkte
 * nicht."* Man fasst sie ein-, zweimal im Jahr an.
 *
 * Eine Wahl wirkt sofort, ohne Bestätigung — wie die Schalter daneben.
 */
export function Zahlenwahl({
  zahlenformat,
  vorzeichen,
  minusfarbe,
  aufZahlenformat,
  aufVorzeichen,
  aufMinusfarbe,
}: Zahlenwahlangabe) {
  return (
    <fieldset className="profilgruppe zahlenwahl">
      <legend>{t('profil.zahlen')}</legend>
      <p className="profilhinweis">{t('profil.zahlen.hinweis')}</p>

      {/*
        **Beträge als Soll und Haben** (Auftrag vom 2026-09-27): „Wir arbeiten
        mit Buchhaltern. Da ist das sehr wichtig." Ein Minuszeichen ist eine
        Rechengröße; Soll und Haben sind die Sprache der doppelten
        Buchführung. Die Wahl gilt für die ganze Anwendung.
      */}
      <div className="feld">
        <label htmlFor="profil-vorzeichen">{t('kopf.vorzeichen')}</label>
        <select
          id="profil-vorzeichen"
          value={vorzeichen}
          onChange={(e) => aufVorzeichen(e.target.value as Vorzeichenart)}
        >
          {VORZEICHENWAHL.map((wahl) => (
            <option key={wahl} value={wahl}>
              {t(`vorzeichen.${wahl}` as Sprachschluessel)}
            </option>
          ))}
        </select>
      </div>

      {/*
        **Negative Beträge rot oder schwarz** (Auftrag vom 2026-10-04: „Reine
        Geschmackssache."). Neben den Vorzeichen, weil es dieselbe Frage ist:
        wie ein Minus aussieht.
      */}
      <div className="feld">
        <label htmlFor="profil-minusfarbe">{t('kopf.minusfarbe')}</label>
        <select
          id="profil-minusfarbe"
          value={minusfarbe}
          onChange={(e) => aufMinusfarbe(e.target.value as Minusfarbe)}
        >
          {MINUSFARBWAHL.map((wahl) => (
            <option key={wahl} value={wahl}>
              {t(`minusfarbe.${wahl}` as Sprachschluessel)}
            </option>
          ))}
        </select>
      </div>

      <div className="feld">
        <label htmlFor="profil-zahlen">{t('kopf.zahlen')}</label>
        <select
          id="profil-zahlen"
          value={zahlenformat}
          onChange={(e) => aufZahlenformat(e.target.value as Zahlenformat)}
        >
          {ZAHLENFORMATWAHL.map((wahl) => (
            <option key={wahl} value={wahl}>
              {/*
                Das Beispiel steht vor dem Namen: „1.234.567,89" erklärt sich
                selbst, „deutsche Schreibweise" muss man erst zuordnen.
              */}
              {wahl === 'sprache'
                ? `${zahlenbeispiel('sprache')} · ${t('zahlenformat.sprache')}`
                : zahlenbeispiel(wahl)}
            </option>
          ))}
        </select>
      </div>
    </fieldset>
  )
}
